import { httpClient } from './http-client';

export interface WalletData {
  id: string;
  doctorId: string;
  available: number;
  pending: number;
  blocked: number;
  createdAt: string;
  updatedAt: string;
}

export interface WalletBalance {
  available: number;
  pending: number;
  blocked: number;
}

export interface WalletTransaction {
  id: string;
  walletId: string;
  type: string;
  amount: number;
  reference?: string;
  referenceId?: string;
  description?: string;
  createdAt: string;
}

export interface PaginatedTransactions {
  items: WalletTransaction[];
  total: number;
}

export interface PayoutData {
  id: string;
  walletId: string;
  amount: number;
  status: string;
  pixKey?: string;
  pixKeyType?: string;
  processedAt?: string;
  createdAt: string;
}

export interface PixKeyData {
  id: string;
  doctorId: string;
  type: 'CPF' | 'CNPJ' | 'EMAIL' | 'PHONE' | 'EVP';
  key: string;
  maskedKey: string;
  createdAt: string;
  verifiedAt: string | null;
}

export interface CreatePixKeyPayload {
  type: 'CPF' | 'CNPJ' | 'EMAIL' | 'PHONE' | 'EVP';
  key: string;
}

export interface UpdatePixKeyPayload {
  type?: 'CPF' | 'CNPJ' | 'EMAIL' | 'PHONE' | 'EVP';
  key?: string;
}

export interface BankAccountData {
  id: string;
  doctorId: string;
  bankName: string;
  bankCode: string;
  agency: string;
  account: string;
  accountType: 'CHECKING' | 'SAVINGS';
  holderName: string;
  holderCpf: string;
  isDefault: boolean;
  isVerified: boolean;
  createdAt: string;
  updatedAt: string;
  maskedAccount: string;
  maskedAgency: string;
  maskedHolderCpf: string;
}

export interface CreateBankAccountPayload {
  bankName: string;
  bankCode: string;
  agency: string;
  account: string;
  accountType: 'CHECKING' | 'SAVINGS';
  holderName: string;
  holderCpf: string;
  isDefault?: boolean;
}

export interface UpdateBankAccountPayload {
  bankName?: string;
  bankCode?: string;
  agency?: string;
  account?: string;
  accountType?: 'CHECKING' | 'SAVINGS';
  holderName?: string;
  holderCpf?: string;
  isDefault?: boolean;
}

export interface PaginatedPayouts {
  items: PayoutData[];
  total: number;
}

export interface WithdrawalRequest {
  amount: number;
  pixKey: string;
  pixKeyType: string;
}

export const walletApi = {
  async getWallet(): Promise<WalletData> {
    return httpClient.get<WalletData>('/wallet/me');
  },

  async getBalance(): Promise<WalletBalance> {
    return httpClient.get<WalletBalance>('/wallet/me/balance');
  },

  async getStatement(page?: number, limit?: number): Promise<PaginatedTransactions> {
    return httpClient.get<PaginatedTransactions>(`/wallet/me/statement${page ? `?page=${page}` : ''}${limit ? `&limit=${limit}` : ''}`);
  },

  async getAvailableBalance(): Promise<{ available: number }> {
    return httpClient.get<{ available: number }>('/wallet/me/available');
  },

  async getPendingBalance(): Promise<{ pending: number }> {
    return httpClient.get<{ pending: number }>('/wallet/me/pending');
  },

  async requestWithdrawal(payload: WithdrawalRequest): Promise<PayoutData> {
    return httpClient.post<PayoutData>('/wallet/withdraw', payload);
  },

  async getWithdrawals(page?: number, limit?: number): Promise<PaginatedPayouts> {
    return httpClient.get<PaginatedPayouts>(`/wallet/me/withdrawals${page ? `?page=${page}` : ''}${limit ? `&limit=${limit}` : ''}`);
  },

  async getPixKey(): Promise<PixKeyData | null> {
    return httpClient.get<PixKeyData | null>('/pix-keys/me');
  },

  async createPixKey(payload: CreatePixKeyPayload): Promise<PixKeyData> {
    return httpClient.post<PixKeyData>('/pix-keys', payload);
  },

  async updatePixKey(id: string, payload: UpdatePixKeyPayload): Promise<PixKeyData> {
    return httpClient.put<PixKeyData>(`/pix-keys/${id}`, payload);
  },

  async deletePixKey(id: string): Promise<void> {
    return httpClient.delete(`/pix-keys/${id}`);
  },

  async validatePixKey(id: string): Promise<{ valid: boolean }> {
    return httpClient.post<{ valid: boolean }>(`/pix-keys/${id}/validate`, {});
  },

  async getBankAccounts(): Promise<BankAccountData[]> {
    return httpClient.get<BankAccountData[]>('/bank-accounts/me');
  },

  async getBankAccount(id: string): Promise<BankAccountData> {
    return httpClient.get<BankAccountData>(`/bank-accounts/${id}`);
  },

  async getDefaultBankAccount(): Promise<BankAccountData | null> {
    return httpClient.get<BankAccountData | null>('/bank-accounts/me/default');
  },

  async createBankAccount(payload: CreateBankAccountPayload): Promise<BankAccountData> {
    return httpClient.post<BankAccountData>('/bank-accounts', payload);
  },

  async updateBankAccount(id: string, payload: UpdateBankAccountPayload): Promise<BankAccountData> {
    return httpClient.put<BankAccountData>(`/bank-accounts/${id}`, payload);
  },

  async deleteBankAccount(id: string): Promise<void> {
    return httpClient.delete(`/bank-accounts/${id}`);
  },

  async validateBankAccount(id: string): Promise<{ valid: boolean; errors: string[] }> {
    return httpClient.post<{ valid: boolean; errors: string[] }>(`/bank-accounts/${id}/validate`, {});
  },

  async setDefaultBankAccount(id: string): Promise<BankAccountData> {
    return httpClient.post<BankAccountData>(`/bank-accounts/${id}/default`, {});
  },
};
